"use client";

import type { FilterFormValues } from "@/components/filters/FilterDropdown";
import type { ListGroupingOption } from "@/types/list";
import { buildColumnToOrderingMap, isServerListOrderingOption, orderingToSortingState } from "@/utils/list-sorting";
import type { ColumnDef, OnChangeFn, RowSelectionState, SortingState, Updater } from "@tanstack/react-table";
import { functionalUpdate, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { FilterListProps, FilterListStickyColumns } from "./FilterListComponent";
import FilterListComponent, { FilterListInternalToolbar } from "./FilterListComponent";
function getFilterListRowId(row: unknown, index: number) {
  if (typeof row === "object" && row !== null && "id" in row) {
    return String((row as {
      id: string | number;
    }).id);
  }
  return String(index);
}
export function retainSelectedListRows<Row>(previous: ReadonlyMap<string, Row>, page: Row[], selection: RowSelectionState) {
  const rows = new Map(previous);
  page.forEach((row, index) => {
    const id = getFilterListRowId(row, index);
    if (selection[id]) rows.set(id, row);
  });
  for (const id of rows.keys()) {
    if (!selection[id]) rows.delete(id);
  }
  return rows;
}
function areSortingStatesEqual(left: SortingState, right: SortingState) {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((entry, index) => {
    const otherEntry = right[index];
    return otherEntry !== undefined && entry.id === otherEntry.id && entry.desc === otherEntry.desc;
  });
}
function resolveSortingStateUpdate(updater: Updater<SortingState>, currentSorting: SortingState) {
  return functionalUpdate(updater, currentSorting);
}
export function orderFilterListColumns<$Row>(columns: Array<ColumnDef<$Row>>, visibleColumnIds?: string[], stickyColumns?: FilterListStickyColumns) {
  const visibleColumnOrder = visibleColumnIds ? new Map(visibleColumnIds.map((columnId, index) => [columnId, index])) : null;
  const leftStickyOrder = new Map((stickyColumns?.left ?? []).map((columnId, index) => [columnId, index]));
  const rightStickyOrder = new Map((stickyColumns?.right ?? []).map((columnId, index) => [columnId, index]));
  return columns.map((column, index) => ({
    column,
    index
  })).filter(({
    column
  }) => {
    if (!visibleColumnOrder || !column.id) {
      return true;
    }
    return visibleColumnOrder.has(String(column.id));
  }).slice().sort((left, right) => {
    const leftColumnId = left.column.id ? String(left.column.id) : null;
    const rightColumnId = right.column.id ? String(right.column.id) : null;
    const leftBucket = leftColumnId && leftStickyOrder.has(leftColumnId) ? 0 : leftColumnId && rightStickyOrder.has(leftColumnId) ? 2 : 1;
    const rightBucket = rightColumnId && leftStickyOrder.has(rightColumnId) ? 0 : rightColumnId && rightStickyOrder.has(rightColumnId) ? 2 : 1;
    if (leftBucket !== rightBucket) {
      return leftBucket - rightBucket;
    }
    if (leftBucket === 0 && leftColumnId && rightColumnId) {
      return (leftStickyOrder.get(leftColumnId) ?? left.index) - (leftStickyOrder.get(rightColumnId) ?? right.index);
    }
    if (leftBucket === 2 && leftColumnId && rightColumnId) {
      return (rightStickyOrder.get(leftColumnId) ?? left.index) - (rightStickyOrder.get(rightColumnId) ?? right.index);
    }
    const leftOrder = leftColumnId && visibleColumnOrder ? visibleColumnOrder.get(leftColumnId) : undefined;
    const rightOrder = rightColumnId && visibleColumnOrder ? visibleColumnOrder.get(rightColumnId) : undefined;
    if (leftOrder !== undefined && rightOrder !== undefined) {
      return leftOrder - rightOrder;
    }
    if (leftOrder !== undefined) {
      return -1;
    }
    if (rightOrder !== undefined) {
      return 1;
    }
    return left.index - right.index;
  }).map(({
    column
  }) => column);
}

/** Use when a page lists records with filters, sorting, selection and pagination. */
export default function FilterList<$Row, $Filters extends FilterFormValues>(props: FilterListProps<$Row, $Filters>) {
  const [selection, setSelection] = React.useState<{
    rowSelection: RowSelectionState;
    rows: Map<string, $Row>;
  }>({
    rowSelection: {},
    rows: new Map()
  });
  const {
    rowSelection
  } = selection;
  const enableRowSelection = props.enableRowSelection ?? true;
  const viewMode = props.viewMode ?? "table";
  const toolbarSlot = props.toolbarPlacement === "external" ? null : <FilterListInternalToolbar filterConfigs={props.filterConfigs} filterInput={props.filterInput} filterComponents={props.filterComponents} onFilterChange={props.onFilterChange} currentFilters={props.currentFilters} exportMutation={props.exportMutation} />;
  const mergedData = useMemo(() => {
    if (!props.computedData?.length) {
      return props.data;
    }
    const computedRowsById = new Map(props.computedData.map((item, index) => [getFilterListRowId(item, index), item]));
    return props.data.map((item_0, index_0) => {
      const computedRow = computedRowsById.get(getFilterListRowId(item_0, index_0));
      if (!computedRow || typeof item_0 !== "object" || item_0 === null) {
        return item_0;
      }
      return {
        ...item_0,
        ...computedRow
      };
    });
  }, [props.data, props.computedData]);
  const groupingOption = useMemo<ListGroupingOption<$Row> | null>(() => props.groupingOption ?? null, [props.groupingOption]);
  const visibleColumns = useMemo(() => {
    const ordered = orderFilterListColumns(props.columns, props.visibleColumnIds, props.stickyColumns);
    const groups = props.columnHeaderGroups;
    if (!groups) return ordered;
    const groupFor = (column: ColumnDef<$Row>) => groups[column.id ?? ("accessorKey" in column ? String(column.accessorKey).replaceAll(".", "_") : "")];
    return ordered.map((column_0, index_1) => ({
      ...column_0,
      meta: {
        ...column_0.meta,
        columnGroupEnd: !!groupFor(column_0) && groupFor(column_0) !== (ordered[index_1 + 1] ? groupFor(ordered[index_1 + 1]!) : undefined)
      }
    }));
  }, [props.columns, props.stickyColumns, props.visibleColumnIds, props.columnHeaderGroups]);

  // Convert display menu orderBy/orderDirection into TanStack SortingState
  const orderingOptions = useMemo(() => props.presentationConfig?.orderingOptions ?? [], [props.presentationConfig?.orderingOptions]);
  const initialSorting = useMemo<SortingState>(() => orderingToSortingState(props.orderBy, props.orderDirection, orderingOptions), [props.orderBy, props.orderDirection, orderingOptions]);
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const columnToOrdering = useMemo(() => buildColumnToOrderingMap(orderingOptions), [orderingOptions]);
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(updater => {
    const nextSorting = resolveSortingStateUpdate(updater, sorting);
    const primarySort = nextSorting[0];
    setSorting(nextSorting);
    if (!props.onOrderChange) {
      return;
    }
    if (!primarySort) {
      props.onOrderChange(null, "asc");
      return;
    }
    const orderingOption = columnToOrdering.get(primarySort.id);
    if (orderingOption && isServerListOrderingOption(orderingOption)) {
      props.onOrderChange(orderingOption.key, primarySort.desc ? "desc" : "asc");
    }
  }, [columnToOrdering, props.onOrderChange, sorting]);
  useEffect(() => {
    setSorting(currentSorting => {
      if (areSortingStatesEqual(currentSorting, initialSorting)) {
        return currentSorting;
      }
      return initialSorting;
    });
  }, [initialSorting]);
  useEffect(() => {
    setSelection({
      rowSelection: {},
      rows: new Map()
    });
  }, [props.selectionScopeKey]);
  const table = useReactTable({
    ...props.tableSearchParams,
    data: mergedData,
    columns: visibleColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: props.manualSorting ?? false,
    autoResetPageIndex: false,
    enableRowSelection,
    enableSorting: true,
    getRowId: getFilterListRowId,
    state: {
      rowSelection,
      sorting
    },
    onRowSelectionChange: updater_0 => {
      setSelection(previous => {
        const next = functionalUpdate(updater_0, previous.rowSelection);
        const rows = retainSelectedListRows(previous.rows, mergedData, next);
        return {
          rowSelection: next,
          rows
        };
      });
    },
    onSortingChange: handleSortingChange
  });
  return <FilterListComponent table={table} selectedRows={[...selection.rows.values()]} toolbarSlot={toolbarSlot} viewMode={viewMode} groupingOption={groupingOption} selectionScopeKey={props.selectionScopeKey} currentSearch={props.currentSearch} {...props} />;
}